import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

interface ShareClass {
  id: string;
  name: string;
  class_type: string;
  authorized_shares: number;
  is_preferred: boolean | null;
  liquidation_pref_multiple: number | null;
  par_value: number | null;
  participation: string | null;
  voting_rights: boolean | null;
}

interface ShareClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  companyId: string;
  shareClass?: ShareClass | null;
}

export const ShareClassDialog = ({
  open,
  onOpenChange,
  onSuccess,
  companyId,
  shareClass,
}: ShareClassDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    class_type: 'common' as 'common' | 'preferred' | 'convertible_note' | 'safe',
    authorized_shares: 0,
    is_preferred: false,
    liquidation_pref_multiple: null as number | null,
    par_value: null as number | null,
    participation: null as string | null,
    voting_rights: true,
  });

  useEffect(() => {
    if (shareClass) {
      setFormData({
        name: shareClass.name,
        class_type: shareClass.class_type as any,
        authorized_shares: shareClass.authorized_shares,
        is_preferred: shareClass.is_preferred ?? false,
        liquidation_pref_multiple: shareClass.liquidation_pref_multiple,
        par_value: shareClass.par_value,
        participation: shareClass.participation,
        voting_rights: shareClass.voting_rights ?? true,
      });
    } else {
      setFormData({
        name: '',
        class_type: 'common',
        authorized_shares: 0,
        is_preferred: false,
        liquidation_pref_multiple: null,
        par_value: null,
        participation: null,
        voting_rights: true,
      });
    }
  }, [shareClass, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        company_id: companyId,
      };

      if (shareClass) {
        const { error } = await supabase
          .from('share_classes')
          .update(payload)
          .eq('id', shareClass.id);

        if (error) throw error;
        toast.success('Share class updated');
      } else {
        const { error } = await supabase.from('share_classes').insert(payload);

        if (error) throw error;
        toast.success('Share class created');
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error saving share class:', error);
      toast.error(error.message || 'Failed to save share class');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{shareClass ? 'Edit' : 'Create'} Share Class</DialogTitle>
            <DialogDescription>Configure the properties for this share class</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Class Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Series A Preferred"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="class_type">Type</Label>
              <Select
                value={formData.class_type}
                onValueChange={(value: any) => setFormData({ ...formData, class_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="common">Common</SelectItem>
                  <SelectItem value="preferred">Preferred</SelectItem>
                  <SelectItem value="convertible_note">Convertible Note</SelectItem>
                  <SelectItem value="safe">SAFE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="authorized_shares">Authorized Shares</Label>
              <Input
                id="authorized_shares"
                type="number"
                value={formData.authorized_shares}
                onChange={(e) =>
                  setFormData({ ...formData, authorized_shares: parseInt(e.target.value) || 0 })
                }
                min="0"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_preferred">Preferred Class</Label>
              <Switch
                id="is_preferred"
                checked={formData.is_preferred}
                onCheckedChange={(checked) => setFormData({ ...formData, is_preferred: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="voting_rights">Voting Rights</Label>
              <Switch
                id="voting_rights"
                checked={formData.voting_rights}
                onCheckedChange={(checked) => setFormData({ ...formData, voting_rights: checked })}
              />
            </div>

            {formData.is_preferred && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="liquidation_pref_multiple">Liquidation Preference Multiple</Label>
                  <Input
                    id="liquidation_pref_multiple"
                    type="number"
                    step="0.1"
                    value={formData.liquidation_pref_multiple || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        liquidation_pref_multiple: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    min="0"
                    placeholder="e.g., 1.0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="participation">Participation Rights</Label>
                  <Select
                    value={formData.participation || 'none'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, participation: value === 'none' ? null : value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="full">Full Participation</SelectItem>
                      <SelectItem value="capped">Capped Participation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="par_value">Par Value (Optional)</Label>
              <Input
                id="par_value"
                type="number"
                step="0.01"
                value={formData.par_value || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    par_value: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                min="0"
                placeholder="e.g., 0.01"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : shareClass ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
